/**
 * The planning assumptions every capacity figure on the screen was calculated
 * against (R3.2, R6.4, Assumption A13).
 *
 * ## Disclosure, not a form
 *
 * These four values are read-only here, and that is a statement about the API
 * rather than a placeholder. Changing the productive-hours rate or the availability
 * factor recalculates every affected baseline record from the effective date (R3.6)
 * — a Recalculation_Run with its own audit trail and inline-versus-background job
 * semantics — so it is a write path in its own right, not a `PUT` this card could
 * pair with. `GET /api/manpower/planning-assumptions` exists; the write does not.
 *
 * Rendering inputs and a save button that could not save would be worse than
 * showing the values plainly: it would promise an edit the product cannot honour.
 * The recalculation notice is kept, because it is the reason the edit is governed
 * and the reader should know what changing one of these would cost.
 *
 * The over-capacity threshold is shown alongside the warning threshold but marked
 * fixed: `classifyBottleneck` calls a period Over_Capacity above 100 percent, and
 * "demand beyond the capacity available" is what that means rather than a number
 * someone chose.
 */
import { useTranslation } from 'react-i18next';
import { AlertTriangleIcon } from 'lucide-react';
import { Card } from '@/components/ui/card';
import type { PlanningAssumptions } from './planning-assumptions-data';

export interface PlanningAssumptionsCardProps {
  readonly assumptions: PlanningAssumptions | undefined;
  readonly isPending: boolean;
  readonly isError: boolean;
}

export function PlanningAssumptionsCard({
  assumptions,
  isPending,
  isError,
}: PlanningAssumptionsCardProps): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <Card className="gap-1 p-5">
      <p className="text-txt text-[13px] font-semibold">
        {t('manpower.capacityInputs.assumptions.heading')}
      </p>
      <p className="text-muted-foreground text-[11.5px]">
        {t('manpower.capacityInputs.assumptions.subtitle')}
      </p>

      {isError ? (
        <p className="text-muted-foreground mt-3 text-[12.3px]">
          {t('manpower.capacityInputs.assumptions.loadError')}
        </p>
      ) : isPending || assumptions === undefined ? (
        <p className="text-muted-foreground mt-3 text-[12.3px]">{t('common.loading')}</p>
      ) : (
        <dl className="mt-3 flex flex-col gap-3">
          <Assumption
            term={t('manpower.capacityInputs.assumptions.productiveHours')}
            value={assumptions.productiveHoursPerWorkday.toFixed(2)}
            hint={t('manpower.capacityInputs.assumptions.productiveHoursHint')}
          />
          <Assumption
            term={t('manpower.capacityInputs.assumptions.availability')}
            value={assumptions.availabilityFactor.toFixed(2)}
            hint={t('manpower.capacityInputs.assumptions.availabilityHint')}
          />
          <Assumption
            term={t('manpower.capacityInputs.assumptions.allocationMethod')}
            // The configured identifier resolved through the catalogue, falling back
            // to the raw value: a method added to configuration before this screen
            // knows its name should read as `milestone` rather than as blank.
            value={
              assumptions.allocationMethod === 'even'
                ? t('manpower.capacityInputs.assumptions.allocationEven')
                : assumptions.allocationMethod
            }
            hint={t('manpower.capacityInputs.assumptions.allocationMethodHint')}
          />
          <Assumption
            term={t('manpower.capacityInputs.assumptions.thresholds')}
            value={t('manpower.capacityInputs.assumptions.thresholdPair', {
              warning: assumptions.capacityThresholdPct,
              over: assumptions.overCapacityThresholdPct,
            })}
            hint={t('manpower.capacityInputs.assumptions.thresholdsHint')}
          />
        </dl>
      )}

      {/* UI spec §3.8's governance tier: the amber fill, border and text tokens. */}
      <p className="mt-4 flex items-start gap-2 rounded-[var(--radius-control)] border border-[var(--a-gov-border)] bg-[var(--a-gov-bg)] p-2 text-[11.5px] leading-[1.5] text-[var(--a-gov-txt)]">
        <AlertTriangleIcon className="mt-[1px] size-3.5 shrink-0" aria-hidden />
        <span>{t('manpower.capacityInputs.assumptions.recalculationNotice')}</span>
      </p>
      <p className="text-muted-foreground mt-2 text-[11px]">
        {t('manpower.capacityInputs.assumptions.readOnlyNote')}
      </p>
    </Card>
  );
}

/** One assumption: its name, the value in force, and what the value means. */
function Assumption({
  term,
  value,
  hint,
}: {
  readonly term: string;
  readonly value: string;
  readonly hint: string;
}): React.JSX.Element {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-label text-[10.5px] font-semibold tracking-wide uppercase">{term}</dt>
      <dd className="text-txt text-[13px] font-semibold tabular-nums">{value}</dd>
      <dd className="text-muted-foreground text-[11px] leading-[1.45]">{hint}</dd>
    </div>
  );
}
